import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import { decodeToken } from '@/lib/utils';
import { RoleType, Permission, CompanyAccountType } from '@hr-outsourcing/shared';
import { extractPermissionsFromMemberships, type Membership } from '@/lib/permissionMapping';

import i18n from '@/lib/i18n';
import { EntityId } from '@/types/common';
export interface User {
  id: EntityId;
  email: string;
  companyEmail?: string | null;
  companyName?: string | null;
  // Company account type (LISTING_ONLY | FULL); null for users without a company.
  accountType?: CompanyAccountType | null;
  firstName?: string | null;
  lastName?: string | null;
  profilePictureUrl?: string | null;
  role: Role;
  companyId?: EntityId;
  language?: string | null;
  employeeId?: EntityId | null;
  employee?: {
    id: EntityId;
    firstName: string;
    lastName: string;
    position: string;
    department: string;
    availability: string;
  } | null;
  permissions?: Permission[];
  memberships?: Membership[];
  isSuperAdmin?: boolean;
  isCompany?: boolean;
  // Only whether a calendar is connected; the raw OAuth refresh tokens are never
  // sent to the browser anymore. (F23)
  hasGoogleCalendar?: boolean;
  hasOutlookCalendar?: boolean;
  // The company's trial once it started (full-use companies): the banner counts down to endsAt.
  trial?: { endsAt: string; active: boolean } | null;
  seatStatus?: string | null;   // 'ACTIVE' | 'RESERVED' | null — from seat-pack subscription system
  leadingTeams?: Array<{
    id: EntityId;
    name: string;
  }>;
}
export interface Role {
  id: EntityId;
  name: RoleType;
  description: string;
}
interface AuthState {
  token: string | null;
  user: User | null;
  loading: boolean;
  _hasHydrated: boolean;
}
interface AuthActions {
  setToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  initAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  // Signs in with a session the server already issued (e.g. right after self sign-up).
  loginWithSession: (session: { token: string; user: unknown }) => void;
  logout: (silent?: boolean) => Promise<void>;
  refreshUser: () => Promise<void>;
}
type AuthStore = AuthState & AuthActions;
const normalizeUserData = (responseData: any) => {
  return (
    responseData?.data?.userDto ??
    responseData?.userDto ??
    responseData?.data?.data?.userDto ??
    responseData?.data?.user ??
    responseData?.user ??
    responseData?.data
  );
};
const mapUserDataToState = (userData: any, token: string | null, storedUser: User | null = null): User | null => {
  if (!userData && !storedUser) return null;
  const sourceData = userData || storedUser;
  const decodedToken = token ? decodeToken(token) : null;
  const isSuperAdmin = decodedToken?.isSuperAdmin || false;
  const isCompany = sourceData?.role?.name === RoleType.Friendly;
  const memberships: Membership[] = sourceData?.memberships ?? [];
  const permissions = extractPermissionsFromMemberships(memberships);
  const leadingTeams = memberships.flatMap(
    (m: {
      leadingTeams?: Array<{
        id: EntityId;
        name: string;
      }>;
    }) => m.leadingTeams ?? []
  );
  return {
    ...sourceData,
    employee: sourceData?.employee ?? null,
    firstName: sourceData?.firstName ?? sourceData?.employee?.firstName ?? null,
    lastName: sourceData?.lastName ?? sourceData?.employee?.lastName ?? null,
    permissions,
    memberships,
    companyId: sourceData?.companyId ?? undefined,
    companyName: sourceData?.companyName ?? sourceData?.Company?.name ?? undefined,
    employeeId: sourceData?.employeeId ?? undefined,
    isSuperAdmin,
    isCompany,
    seatStatus: sourceData?.seatStatus ?? null,
    trial: sourceData?.trial ?? null,
    accountType: sourceData?.accountType ?? null,
    language: sourceData?.language ?? null,
    role: sourceData?.role,
    profilePictureUrl: sourceData?.profilePictureUrl ?? null,
    hasGoogleCalendar: !!sourceData?.hasGoogleCalendar,
    hasOutlookCalendar: !!sourceData?.hasOutlookCalendar,
    leadingTeams,
  };
};
export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        token: null,
        user: null,
        loading: true,
        _hasHydrated: false,
        setToken: (token) => set({ token }),
        setUser: (user) => set({ user }),
        initAuth: async () => {
          const { token, user: storedUser } = get();
          if (token) {
            try {
              const response = await api.get('/auth/me');
              const userData = normalizeUserData(response.data);
              const userWithSuperAdmin = mapUserDataToState(userData, token, storedUser);
              if (userWithSuperAdmin) {
                set({ user: userWithSuperAdmin, loading: false });
              } else {
                set({ loading: false });
              }
            } catch (_err) {
              set({ token: null, user: null, loading: false });
            }
          } else {
            set({ loading: false });
          }
        },
        login: async (email, password) => {
          try {
            const response = await api.post('/auth/login', { email, password });
            const { token, user: rawUser } = response.data.data;
            const userWithSuperAdmin = mapUserDataToState(rawUser, token);
            if (!userWithSuperAdmin) throw new Error('Failed to process user data');
            set({ token, user: userWithSuperAdmin });
            toast.success(i18n.t('auth.messages.loginSuccess'));
          } catch (error: unknown) {
            throw error;
          }
        },
        loginWithSession: ({ token, user: rawUser }) => {
          const mapped = mapUserDataToState(rawUser, token);
          if (!mapped) throw new Error('Failed to process user data');
          set({ token, user: mapped });
        },
        logout: async (silent = false) => {
          try {
            await api.post('/auth/logout');
          } catch (_error) {
          } finally {
            set({ token: null, user: null });
            sessionStorage.clear();
            try {
              const persistentKeys = ['i18nextLng'];
              const preserved = persistentKeys
                .map((key) => [key, localStorage.getItem(key)] as const)
                .filter(([, value]) => value !== null);
              localStorage.clear();
              for (const [key, value] of preserved) {
                localStorage.setItem(key, value as string);
              }
            } catch (_storageError) {}
            if (!silent) {
              toast.success(i18n.t('auth.messages.logoutSuccess'));
            }
          }
        },
        refreshUser: async () => {
          try {
            const response = await api.get('/auth/me');
            const userData = normalizeUserData(response.data);
            if (!userData) return;
            const { token, user: currentUser } = get();
            const updatedUser = mapUserDataToState(userData, token || '', currentUser);
            if (updatedUser) {
              set({ user: updatedUser });
            }
          } catch (error) {
            console.error('Failed to refresh user:', error);
          }
        },
      }),
      {
        name: 'auth-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          token: state.token,
          // Defence-in-depth: the server no longer sends raw refresh tokens (F23),
          // but strip them here too so a rollout-skew response that still carries
          // them can never be persisted into localStorage.
          user: state.user
            ? ({
                ...state.user,
                googleRefreshToken: undefined,
                outlookRefreshToken: undefined,
              } as typeof state.user)
            : null,
        }),
        onRehydrateStorage: () => (state, error) => {
          if (error) {
            console.error('Auth hydration failed:', error);
          }
          queueMicrotask(() => {
            useAuthStore.setState({ _hasHydrated: true });
          });
        },
      }
    ),
    { name: 'AuthStore', enabled: import.meta.env.DEV }
  )
);
export const getToken = () => useAuthStore.getState().token;
export const getUser = () => useAuthStore.getState().user;
export const getAuthHeader = () => {
  const token = getToken();
  return token ? `Bearer ${token}` : null;
};
export const hasHydrated = () => useAuthStore.getState()._hasHydrated;
export const waitForHydration = (): Promise<void> => {
  return new Promise((resolve) => {
    if (useAuthStore.getState()._hasHydrated) {
      resolve();
      return;
    }
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state._hasHydrated) {
        unsubscribe();
        resolve();
      }
    });
  });
};
